import { useCallback, useContext, useEffect, useReducer, useRef } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const emptyState = {
  identity: null, items: [], loading: false, error: null, requestId: null, events: [],
};
const hasId = (item) => item?.id !== undefined && item.id !== null;
const sameCompany = (item, companyId) =>
  item?.companyId == null || String(item.companyId) === String(companyId);

const updateItems = (items, action) => {
  if (action.type === "DELETE") {
    return items.filter(item => String(item.id) !== String(action.payload));
  }
  const item = action.payload;
  const index = items.findIndex(current => String(current.id) === String(item.id));
  if (index === -1) {
    // Um evento parcial de sessão aguarda o snapshot, que contém os metadados da linha.
    if (action.type === "SESSION" && (!item.name || !item.channel)) return items;
    return [item, ...items];
  }
  return items.map((current, position) =>
    position === index ? { ...current, ...item } : current
  );
};

const reducer = (state, action) => {
  if (action.type === "RESET") {
    return { ...emptyState, identity: action.identity, loading: Boolean(action.identity) };
  }
  if (state.identity !== action.identity) return state;
  if (action.type === "REQUEST") {
    return { ...state, requestId: action.requestId, loading: true, error: null, events: [] };
  }
  if (action.type === "LOAD" || action.type === "ERROR") {
    if (state.requestId !== action.requestId) return state;
    return {
      ...state,
      items: action.type === "LOAD"
        ? state.events.reduce(updateItems, action.payload)
        : state.items,
      loading: false,
      error: action.type === "ERROR" ? "Não foi possível carregar as conexões. Tente novamente." : null,
      requestId: null,
      events: [],
    };
  }
  if (["UPDATE", "SESSION", "DELETE"].includes(action.type)) {
    return {
      ...state,
      items: updateItems(state.items, action),
      // O snapshot pode ter sido lido antes de um evento recebido durante o GET.
      events: state.requestId === null ? state.events : [...state.events, action],
    };
  }
  return state;
};

const useWhatsApps = () => {
  const { user, socket, isAuth, loading: authLoading } = useContext(AuthContext);
  const companyId = user?.companyId;
  const userId = user?.id;
  const identity = isAuth && !authLoading && userId && companyId
    ? String(companyId) + ":" + String(userId)
    : null;
  const [state, dispatch] = useReducer(reducer, emptyState);
  const mounted = useRef(false);
  const request = useRef(0);
  const identityRef = useRef(identity);
  identityRef.current = identity;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      request.current += 1;
    };
  }, []);

  const reloadWhatsApps = useCallback(async () => {
    if (!identity || !mounted.current || identityRef.current !== identity) return;
    const requestId = ++request.current;
    const isCurrent = () =>
      mounted.current && identityRef.current === identity && request.current === requestId;
    dispatch({ type: "REQUEST", identity, requestId });
    try {
      const { data } = await api.get("/whatsapp/?session=0", { timeout: 15000 });
      if (!isCurrent()) return;
      if (!Array.isArray(data)) throw new Error("ERR_INVALID_CONNECTIONS_RESPONSE");
      dispatch({
        type: "LOAD", identity, requestId,
        payload: data.filter(item => hasId(item) && sameCompany(item, companyId)),
      });
    } catch (error) {
      if (isCurrent()) dispatch({ type: "ERROR", identity, requestId });
    }
  }, [identity, companyId]);

  useEffect(() => {
    dispatch({ type: "RESET", identity });
    if (identity) reloadWhatsApps();
    return () => { request.current += 1; };
  }, [identity, reloadWhatsApps]);

  useEffect(() => {
    if (!identity || !socket || typeof socket.on !== "function" || typeof socket.off !== "function") return;
    const current = () => mounted.current && identityRef.current === identity;
    const onWhatsapp = (data) => {
      if (!current()) return;
      if (data?.action === "update" && hasId(data.whatsapp) && sameCompany(data.whatsapp, companyId)) {
        dispatch({ type: "UPDATE", identity, payload: data.whatsapp });
      } else if (data?.action === "delete" && data.whatsappId != null) {
        dispatch({ type: "DELETE", identity, payload: data.whatsappId });
      }
    };
    const onSession = (data) => {
      if (current() && data?.action === "update" && hasId(data.session) && sameCompany(data.session, companyId)) {
        dispatch({ type: "SESSION", identity, payload: data.session });
      }
    };
    const onConnect = () => { if (current()) reloadWhatsApps(); };
    const whatsappEvent = "company-" + companyId + "-whatsapp";
    const sessionEvent = "company-" + companyId + "-whatsappSession";
    socket.on(whatsappEvent, onWhatsapp);
    socket.on(sessionEvent, onSession);
    socket.on("connect", onConnect);
    return () => {
      socket.off(whatsappEvent, onWhatsapp);
      socket.off(sessionEvent, onSession);
      socket.off("connect", onConnect);
    };
  }, [identity, socket, companyId, reloadWhatsApps]);

  const ownsState = identity !== null && state.identity === identity;
  const whatsApps = ownsState ? state.items : [];
  return {
    whatsApps,
    loading: Boolean(authLoading || (identity && (!ownsState || (state.loading && whatsApps.length === 0)))),
    refreshing: Boolean(ownsState && state.loading),
    error: ownsState ? state.error : null,
    reloadWhatsApps,
  };
};

export default useWhatsApps;
